import React, { forwardRef } from 'react'
import { cn } from '@/lib/utils'
import { useId } from '@/lib/hooks/useAccessibility'
import { createAriaDescribedBy } from '@/lib/accessibility'

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helpText?: string
  fullWidth?: boolean
  required?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helpText, fullWidth = true, className, required, ...props }, ref) => {
    const generatedId = useId('input')
    const inputId = props.id || generatedId
    const errorId = error ? `${inputId}-error` : undefined
    const helpId = helpText ? `${inputId}-help` : undefined
    const describedBy = createAriaDescribedBy([errorId, helpId])

    return (
      <div className={cn('form-field mb-4', fullWidth ? 'w-full' : '')}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'label block text-sm font-medium mb-1',
              required && "after:content-['*'] after:ml-1 after:text-red-500"
            )}
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-describedby={describedBy}
          aria-invalid={error ? 'true' : 'false'}
          aria-required={required}
          className={cn(
            'input w-full px-4 py-2 border rounded-md transition-colors',
            'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700',
            'focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : '',
            className
          )}
          {...props}
        />
        {helpText && (
          <p id={helpId} className="form-help mt-1 text-sm text-gray-600 dark:text-gray-400">
            {helpText}
          </p>
        )}
        {error && (
          <p
            id={errorId}
            className="form-error mt-1 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
